/**
 * Danish text for the per-field validation results returned by cle-compliance-api.
 *
 * The API's `reason` strings are English and are meant for logs and admin
 * diagnostics, so they must never reach a member. Translation happens here,
 * keyed on the exact English string: `participantName` alone emits three
 * different reasons and the distinction matters to the user ("we couldn't read a
 * name off the certificate" vs. "the name doesn't match yours"), which a
 * field-key lookup cannot express.
 *
 * The reason strings are pinned by
 * cle-compliance-api/tests/test_field_validation_reason_contract.py — if that
 * test fails, this map needs the same edit.
 */

import {
  MIN_GUARANTEE_TEXT,
  MIN_LAWYER_LIABILITY_TEXT,
} from "../constants/insuranceRequirements";

const normalizeReason = (reason: string): string =>
  reason.trim().replace(/\s+/g, " ").toLowerCase();

const REASON_TEXT_DA_SOURCE: Record<string, string> = {
  "Document must be a legal course certificate":
    "Dokumentet ser ikke ud til at være et juridisk kursusbevis.",
  "Course is not relevant to the lawyer's professional practice":
    "Kurset er vurderet til ikke at være relevant for dit virke som advokat.",
  "Course relevance could not be determined from the document":
    "Kursets faglige relevans kunne ikke afgøres ud fra beviset.",
  // The AI deliberately did not decide (management, communication, rhetoric):
  // not a verdict on the course, so it must not read as one.
  "Course relevance requires assessment by a case handler":
    "Kursets faglige relevans kan ikke vurderes automatisk og skal vurderes af sekretariatet.",
  "Course provider is required": "Kursusudbyderen fremgår ikke af beviset.",
  "Course date is required": "Kursusdatoen fremgår ikke af beviset.",
  "Course was partly held outside the efteruddannelse period":
    "Kurset er delvist gennemført uden for din efteruddannelsesperiode.",
  "Course end date is before the course date":
    "Kursets slutdato ligger før startdatoen, så kursusperioden kunne ikke aflæses.",
  "Course time is required":
    "Kursets samlede varighed fremgår ikke af beviset.",
  "Number of lessons is required": "Antal lektioner fremgår ikke af beviset.",
  "Course must comprise at least 3 lessons":
    "Kurset skal omfatte mindst 3 lektioner.",
  "Lesson count must be stated as lektioner or kursuslektioner":
    "Antallet skal fremgå af beviset som lektioner eller kursuslektioner.",
  // Retired by the API in favour of the signature-or-logo wording below, but
  // still stored on analysis runs recorded before, so keep it.
  "Certificate must be signed":
    "Beviset er ikke underskrevet af kursusudbyderen.",
  "Certificate must be signed or carry the course provider's logo":
    "Beviset er hverken underskrevet af kursusudbyderen eller forsynet med udbyderens logo eller stempel.",
  "Course description is required":
    "En beskrivelse af kursets indhold fremgår ikke af beviset.",
  "Certificate participant name could not be extracted":
    "Deltagerens navn kunne ikke læses på beviset.",
  "Uploader display name is not available for verification":
    "Dit navn kunne ikke hentes, så deltagernavnet kunne ikke kontrolleres. Kontakt sekretariatet.",
  "Certificate name does not match the uploading user":
    "Deltagernavnet på beviset stemmer ikke med dit navn.",
  "Insurance company is not on the approved list":
    "Forsikringsselskabet står ikke på listen over godkendte selskaber.",
  "Lawyer liability amount must be at least 2,500,000 DKK": `Advokatansvarsdækningen skal være mindst ${MIN_LAWYER_LIABILITY_TEXT}.`,
  "Guarantee amount must be at least 5,000,000 DKK": `Garantibeløbet skal være mindst ${MIN_GUARANTEE_TEXT}.`,
};

const REASON_TEXT_DA: Record<string, string> = Object.fromEntries(
  Object.entries(REASON_TEXT_DA_SOURCE).map(([reason, text]) => [
    normalizeReason(reason),
    text,
  ]),
);

/**
 * Danish labels for validated fields. Used both for rows the UI does not
 * hardcode and as the basis for the per-field fallback text.
 *
 * `courseTime` is the total number of minutes across all lessons — a duration,
 * not a time of day — hence "Kursets varighed".
 */
export const FIELD_LABEL_DA: Record<string, string> = {
  isLegalCourseCertificate: "Juridisk kursusbevis",
  courseTitle: "Kursustitel",
  courseProvider: "Kursusudbyder",
  courseDate: "Kursusdato",
  courseEndDate: "Kursets slutdato",
  coursePeriod: "Kursusperiode",
  courseTime: "Kursets varighed",
  numberOfLessons: "Antal lektioner",
  signature: "Underskrift",
  courseDescription: "Kursusbeskrivelse",
  participantName: "Deltagernavn",
  insuranceCompany: "Forsikringsselskab",
  policyStartDate: "Police startdato",
  lawyerLiabilityAmount: "Advokatansvar",
  guaranteeAmount: "Garantibeløb",
};

const GENERIC_REASON_TEXT = "Feltet mangler eller opfylder ikke kravene.";

/** The API tolerates both camelCase and PascalCase keys, so normalize on lookup. */
export const normalizeFieldKey = (key: string): string =>
  key.length > 0 ? `${key[0].toLowerCase()}${key.slice(1)}` : key;

export const getFieldValidationLabel = (key: string): string =>
  FIELD_LABEL_DA[normalizeFieldKey(key)] ?? key;

/**
 * Resolve Danish text for a failing field: exact reason match, then a
 * label-derived fallback for the field, then a generic message.
 *
 * Never returns the English input — an unrecognized reason means the API
 * reworded a string, and showing English to a member is worse than showing a
 * vaguer Danish sentence.
 */
export const resolveFieldValidationReason = (
  key: string,
  reason: string | null | undefined,
): string => {
  if (reason) {
    const translated = REASON_TEXT_DA[normalizeReason(reason)];

    if (translated) {
      return translated;
    }
  }

  const label = FIELD_LABEL_DA[normalizeFieldKey(key)];

  return label
    ? `${label} mangler eller opfylder ikke kravene.`
    : GENERIC_REASON_TEXT;
};
